/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React from "react";
import ProductCard from "./ProductCard";

export default function ProductList({
  visibleProducts,
  theme,
  viewMode,
  setViewMode,
  sortValue,
  setSortValue,
  isMobile,
  isTablet,
  handleOpenProduct,
  handleAddToCart,
  setProducts
}) {
  // Inject hover styles
  React.useEffect(() => {
    const styleId = 'products-list-hover-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .product-card-container {
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
        }
        .product-card-container:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 30px rgba(0,0,0,0.12) !important;
          border-color: ${theme.primary}44 !important;
        }
        .product-card-container:hover .product-image {
          transform: scale(1.08);
        }
        .add-to-cart-btn:hover {
          filter: brightness(1.1);
          transform: translateY(-2px);
          box-shadow: 0 10px 25px ${theme.primary}66 !important;
        }
        .add-to-cart-btn:active {
          transform: translateY(0);
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const style = document.getElementById(styleId);
      if (style) style.remove();
    };
  }, [theme.primary]);

  if (!visibleProducts || (visibleProducts.length === 0 && !sortValue)) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: "64px", marginBottom: "20px" }}>🔍</div>
        <h3 style={{ color: "#666", marginBottom: "80px" }}>لا توجد منتجات</h3>
      </div>
    );
  }

  const controlsContainerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: isMobile ? "12px" : "25px",
    flexWrap: "wrap",
    gap: isMobile ? "10px" : "15px",
    padding: isMobile ? "10px" : "15px",
    background: "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(10px)",
    borderRadius: "16px",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.02)"
  };

  const selectStyle = {
    padding: "8px 16px",
    borderRadius: "10px",
    border: `1px solid ${theme.primary}33`,
    outline: "none",
    fontSize: "14px",
    fontWeight: "500",
    color: "#444",
    cursor: "pointer",
    background: "white",
    transition: "all 0.3s ease"
  };

  const viewBtnStyle = (active) => ({
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    background: active ? theme.primary : "white",
    color: active ? "white" : "#666",
    border: `1px solid ${active ? theme.primary : "#eee"}`,
    transition: "all 0.3s ease",
    fontSize: "18px"
  });

  return (
    <div style={{ marginBottom: "80px" }}>
      {/* Products Header Controls */}
      <div style={controlsContainerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontWeight: "700", color: "#333", fontSize: "16px" }}>
            {visibleProducts.length} منتج
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          {/* Sorting Dropdown */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#888", fontWeight: "600" }}>ترتيب حسب:</span>
            <select
              style={selectStyle}
              value={sortValue}
              onChange={(e) => setSortValue(e.target.value)}
              onMouseEnter={(e) => (e.target.style.borderColor = theme.primary)}
              onMouseLeave={(e) => {
                if (document.activeElement !== e.target) {
                  e.target.style.borderColor = `${theme.primary}33`;
                }
              }}
            >
              <option value="">الافتراضي</option>
              <option value="alphabetical">الاسم (أ - ي)</option>
              <option value="hightolow">السعر (الأعلى أولاً)</option>
              <option value="lowtohigh">السعر (الأقل أولاً)</option>
              <option value="big_discount">خصم كبير</option>
              <option value="small_discount">خصم قليل</option>
            </select>
          </div>

          {/* View Mode Toggles */}
          <div style={{ display: "flex", gap: "8px", borderRight: isMobile ? "none" : "1px solid #eee", paddingRight: isMobile ? "0" : "12px", marginRight: isMobile ? "0" : "5px" }}>
            <div
              style={viewBtnStyle(viewMode === "grid")}
              onClick={() => setViewMode("grid")}
              title="عرض شبكي"
            >
              <i className="fas fa-th-large"></i>
            </div>
            <div
              style={viewBtnStyle(viewMode === "list")}
              onClick={() => setViewMode("list")}
              title="عرض قائمة"
            >
              <i className="fas fa-list"></i>
            </div>
          </div>
        </div>
      </div>

      {visibleProducts.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p style={{ color: "#999" }}>لا توجد منتجات تطابق خيارات التصفية</p>
        </div>
      ) : (
        <div style={{
          display: viewMode === "grid" ? "grid" : "flex",
          gridTemplateColumns: viewMode === "grid"
            ? isMobile
              ? "repeat(auto-fill, minmax(160px, 1fr))"
              : isTablet
                ? "repeat(auto-fill, minmax(220px, 1fr))"
                : "repeat(auto-fill, minmax(260px, 1fr))"
            : "1fr",
          flexDirection: viewMode === "list" ? "column" : "row",
          gap: isMobile ? "12px" : "20px",
        }}>
          {visibleProducts.map(p => (
            <ProductCard
              key={`product-${p.id}`}
              p={{ ...p, setProducts }}
              theme={theme}
              viewMode={viewMode}
              handleOpenProduct={handleOpenProduct}
              handleAddToCart={handleAddToCart}
            />
          ))}
        </div>
      )}
    </div>
  );
}
